import { Injectable } from '@angular/core';
import { FrameworkHelper } from '@brafton/angular-framework-library';
import { AssetSet } from '@brafton/assets-api';
import { IBaseHasDependent, IBaseTask, People } from '@brafton/common-framework-library';
import { TextSet } from '@brafton/text-api-client';

@Injectable({
  providedIn: 'root',
})
export class ProjectHelper extends FrameworkHelper {
  /**
   * Deteremine if an owner exists within a specific team or not.
   */
  ownerInTeam(owner: People, teamId: number): boolean {
    if (!owner) return false;
    if (owner.id == teamId) return true;

    if (!(owner as any).team) return false;

    const iterate = this.ownerInTeam((owner as any).team as People, teamId);
    return iterate;
  }

  /**
   * Determine the pace of a given month.
   * @param month
   * @returns
   */
  monthPace(month: Date): number {
    const now = new Date();

    // set month to beginning of month
    month.setUTCDate(1);
    month.setUTCHours(0);
    month.setUTCMinutes(0);
    month.setUTCMilliseconds(0);

    // figure out exact end of the month
    const endMonth = new Date(month);
    endMonth.setUTCMonth(month.getUTCMonth() + 1);
    endMonth.setUTCMinutes(endMonth.getUTCMinutes() - 1);

    // if month and year match then must be within the current month
    if (month.getUTCMonth() === now.getUTCMonth() && month.getUTCFullYear() === now.getUTCFullYear()) {
      const totalMonthDuration = endMonth.getTime() - month.getTime();
      const currentPos = now.getTime() - month.getTime();

      const pace = currentPos / totalMonthDuration;

      return pace;
    }
    // passed over month is in a future month so pace must be 0
    else if (now < month) {
      return 0.0;
    }
    // passed over momth must be in a past month so pace must be 1.0
    else {
      return 1.0;
    }
  }

  /***
   * Get the route to the project in the current work tree.
   */
  findRoute(work: any): string {
    if (!work) throw new Error('work is null');
    if (work.type === 'Organization') return `/orgs/${work.id}`;
    if (work.type === 'StatementOfWork') return `/sows/${work.id}`;

    const project = this.findProject(work);

    if (!project || !project.type) throw new Error('No project head found');

    if (project.parent && project.parent.type === 'MasterProject') return `/projects/${project.parent.id}`;

    switch (project.type) {
      case 'ProductionProject': {
        return `/produce/productions/${project.id}`;
      }
      case 'IdeaProject': {
        return `/plan/ideas/${project.id}`;
      }
      case 'OrganizationProject': {
        throw 'no organization target';
      }
      case 'ContentProject': {
        return `/content/${project.id}`;
      }
      case 'CampaignProject': {
        return `/campaigns/${project.id}`;
      }
      case 'MasterProject': {
        return `/projects/${project.id}`;
      }
      default:
        throw new Error('No project head found');
    }
  }

  /**
   * Find the nearest sow node working upwards from a task
   * @param work
   * @returns
   */
  findSow(work: any): any {
    if (!work) return;

    if (work.type === 'StatementOfWork') {
      return work;
    }

    if (!work?.parent) return;

    // iterate
    return this.findSow(work.parent);
  }

  /**
   * Find If Task should be defaulted to private or not
   * @param work
   * @returns boolean
   */
  taskDefaultPublicComment(task: IBaseTask, actionId: string): boolean {
    if (!task) return false;

    if (this.ownerInTeam(task.owner as any, 20)) return true;

    const progressingActions = [
      'Complete',
      'AcceptApproval',
      'Approval',
      'FastForward',
      'CompleteTask',
      'PublishLocation',
      'PublishSimple',
      'PublishContentLibrary',
    ];

    const space = this.findSpace(task);
    const tasks = this.listTasks(space);

    const hasCustomerDependent = tasks.find(
      (t: IBaseTask) =>
        (t as unknown as IBaseHasDependent)?.dependentOn &&
        (t as unknown as IBaseHasDependent)?.dependentOn?.id === task.id &&
        t.owner &&
        this.ownerInTeam(t.owner as unknown as People, 20),
    );

    // found a customer dependent task and the task is owned by the customer - ie customer is the next step
    // so should be public
    if (hasCustomerDependent !== undefined && progressingActions.includes(actionId)) return true;

    return false;
  }

  /**
   * Determine if a assets or text set is ready to be shown in the content slider
   * @param assets, textSets
   * @returns boolean
   */
  showContentSlider(assets: AssetSet[], textSets: TextSet[]): boolean {
    // check assets and text set is empty
    if (assets.length === 0 && textSets.length === 0) {
      return false;
    }
    // check assets set status is ready
    if (assets.length > 0 && assets.some((asset) => asset?.assetFiles[0]?.status?.id === 'Ready')) {
      return true;
    }
    // check text set version number is greater than 0
    if (textSets.length > 0 && textSets.some((text) => text.accessLevel && text.accessLevel.id !== 'System' && text?.version?.versionNumber || 0 > 0)) {
      return true;
    }

    // Set Default state
    return false;
  }
  /**
   * Determine if a assets is ready to be shown in the content slider
   * @param assets, textSets
   * @returns boolean
   */
  showAssetSlider(assets: AssetSet[]): boolean {
    // check assets and text set is empty
    if (assets.length === 0) {
      return false;
    }
    // check assets set status is ready
    if (assets.length > 0 && assets.some((asset) => asset?.assetFiles[0]?.status?.id === 'Ready')) {
      return true;
    }

    // Set Default state
    return false;
  }
}
